
import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { podpislonService, PodpislonSignatureRequest } from '@/services/podpislonService';

export const usePodpislonSignature = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState<'idle' | 'pending' | 'signed' | 'rejected' | 'expired'>('idle');
  const { toast } = useToast();

  const sendForSignature = useCallback(async (request: PodpislonSignatureRequest): Promise<string | null> => {
    setIsLoading(true);
    
    try {
      console.log('[usePodpislonSignature] Sending document for signature:', request.orderId);
      
      const response = await podpislonService.sendForSignature(request);
      
      setSignatureStatus('pending');
      
      toast({
        title: "Документ отправлен",
        description: "СМС с подписью отправлена клиенту через podpislon.ru",
      });

      return response.signatureId;
    } catch (error: unknown) {
      console.error('[usePodpislonSignature] Error sending signature:', error);
      const message = error instanceof Error ? error.message : 'Не удалось отправить документ на подпись';
      toast({
        title: "Ошибка отправки",
        description: message,
        variant: "destructive",
      });
      
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const checkSignatureStatus = useCallback(async (signatureId: string) => {
    try {
      console.log('[usePodpislonSignature] Checking signature status:', signatureId);
      
      const status = await podpislonService.getSignatureStatus(signatureId);
      setSignatureStatus(status.status);
      
      return status;
    } catch (error: unknown) {
      console.error('[usePodpislonSignature] Error checking status:', error);
      const message = error instanceof Error ? error.message : 'Не удалось проверить статус подписи';
      toast({
        title: "Ошибка проверки статуса",
        description: message,
        variant: "destructive",
      });
      
      return null;
    }
  }, [toast]);

  const cancelSignature = useCallback(async (signatureId: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      console.log('[usePodpislonSignature] Cancelling signature:', signatureId);
      
      const success = await podpislonService.cancelSignature(signatureId);
      
      if (success) {
        setSignatureStatus('idle');
        toast({
          title: "Подпись отменена",
          description: "Запрос на подпись успешно отменен",
        });
      }
      
      return success;
    } catch (error: unknown) {
      console.error('[usePodpislonSignature] Error cancelling signature:', error);
      const message = error instanceof Error ? error.message : 'Не удалось отменить подпись';
      toast({
        title: "Ошибка отмены",
        description: message,
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  return {
    isLoading,
    signatureStatus,
    sendForSignature,
    checkSignatureStatus,
    cancelSignature,
    setSignatureStatus
  };
};
